import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getStaffPass } from '@generated/hooks/staffPass';
import { searchAdvertisers } from '@generated/server/contracts';

type Name = Awaited<ReturnType<typeof searchAdvertisers>>['names'][number];

/** Advertiser name box with a suggestion list from past ads and every CRM account/prospect. */
export function AdvertiserInput({ value, onChange, className, label }: { value: string; onChange: (v: string) => void; className: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [names, setNames] = useState<Name[]>([]);
  const [searching, setSearching] = useState(false);
  const typed = value.trim();

  useEffect(() => {
    if (!open || typed.length < 2) { setNames([]); return; }
    let on = true;
    setSearching(true);
    const t = setTimeout(() => {
      searchAdvertisers({ data: { pass: getStaffPass(), q: typed } })
        .then((r) => { if (on) setNames((r?.names ?? []).slice(0, 12)); })
        .catch((e) => console.error(e))
        .finally(() => { if (on) setSearching(false); });
    }, 300);
    return () => { on = false; clearTimeout(t); };
  }, [typed, open]);

  const exact = names.some((n) => n.name.toLowerCase() === typed.toLowerCase());
  return (
    <div className="relative">
      <input aria-label={label ?? 'Advertiser'} className={className} value={value} autoComplete="off" placeholder="Start typing a name…"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && typed.length >= 2 && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto border-2 border-foreground bg-card text-sm font-normal shadow-lg">
          {searching && !names.length && <li className="flex items-center gap-2 px-3 py-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Searching…</li>}
          {!searching && !names.length && <li className="px-3 py-2 text-muted-foreground">No past advertiser or account matches. A new name is fine.</li>}
          {names.map((n) => (
            <li key={n.name}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(n.name); setOpen(false); }}
                className="flex min-h-11 w-full items-center justify-between gap-2 px-3 text-left hover:bg-muted">
                <span className="min-w-0 truncate">{n.name}</span>
                {n.accountId && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">CRM</span>}
              </button>
            </li>
          ))}
          {!exact && names.length > 0 && <li className="border-t border-border px-3 py-2 text-xs text-muted-foreground">Or keep "{typed}" as a new advertiser.</li>}
        </ul>
      )}
    </div>
  );
}
